import { Controller } from '@hotwired/stimulus';

/**
 * Fetches a PNG from `url` for Web Share.
 */
export default class ShareImageController extends Controller {
  public static readonly targets = ['shareButton'];
  public static readonly values = { url: String, filename: String };

  declare private readonly shareButtonTarget: HTMLButtonElement;
  declare private readonly hasShareButtonTarget: boolean;
  declare private readonly urlValue: string;
  declare private readonly filenameValue: string;

  public connect(): void {
    if (!('share' in navigator) && this.hasShareButtonTarget) {
      this.shareButtonTarget.remove();
    }
  }

  public async share(): Promise<void> {
    try {
      if (!('share' in navigator)) {
        return;
      }

      const blob = await this._fetchPngBlob();
      const file = new File([blob], this.filenameValue, {
        type: blob.type || 'image/png'
      });
      const shareData = { files: [file] };

      if ('canShare' in navigator && !navigator.canShare(shareData)) {
        return;
      }

      await navigator.share(shareData);
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error(err.name, err.message);
      }
    }
  }

  private async _fetchPngBlob(): Promise<Blob> {
    const response = await fetch(this.urlValue, {
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      );
    }

    return response.blob();
  }
}
